import { Component } from '@theme/component';
import { throttle, prefersReducedMotion, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantUpdateEvent, HeaderUpdateEvent } from '@theme/events';

const SCROLL_UP_THRESHOLD = 0;
const SCROLL_DOWN_THRESHOLD = 150;

class HeaderComponent extends Component {
  #abortController = new AbortController();
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
    this.style.setProperty('--top-row-height', `${height}px`);
    this.#syncReevysHeaderState();
  });

  #intersectionObserver = null;
  #offscreen = false;
  #lastScrollTop = 0;
  #scrollTimeout = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener(ThemeEvents.headerUpdate, this.#handleHeaderUpdate);
    this.#handleBreakpointChange();
    this.#syncReevysHeaderState();

    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#abortController.abort();

    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  #handleBreakpointChange = () => {
    this.#abortController.abort();
    this.#abortController = new AbortController();

    const stickyMode = this.getAttribute('sticky');
    const isSticky = stickyMode !== 'none';

    if (mediaQueryLarge.matches) {
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
    }

    if (isSticky) {
      const scrollContainer = getIntersectionRoot() || document;
      scrollContainer?.addEventListener('scroll', this.#handleWindowScroll, {
        signal: this.#abortController.signal,
        passive: true,
      });

      window.addEventListener('resize', this.#handleResize, {
        signal: this.#abortController.signal,
      });
    }

    this.#syncReevysHeaderState();
  };

  #handleHeaderUpdate = ({ detail: { reselectBlock = false } }) => {
    if (reselectBlock) {
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
    }

    this.#handleBreakpointChange();
  };

  #handleWindowScroll = throttle(
    onAnimationFrame(() => this.#updateScrollState()),
    50
  );

  #handleResize = throttle(
    onAnimationFrame(() => this.#handleBreakpointChange()),
    50
  );

  #keyframeDuration = 300;

  #calcSpeed = (distance) => {
    const minSpeed = 250;
    const maxSpeed = 300;
    const maxDistance = 100;

    return minSpeed + (maxSpeed - minSpeed) * Math.min(distance / maxDistance, 1);
  };

  #setTransitionProperties = (distanceFromStart) => {
    if (prefersReducedMotion()) return;

    const speed = this.#calcSpeed(distanceFromStart);
    const duration = distanceFromStart / speed;

    this.style.setProperty('--reveal-speed', `${duration}s`);
    this.style.setProperty('--reveal-duration', `${duration * 1000}ms`);
    this.style.setProperty('--animation-duration', `${this.#keyframeDuration}ms`);
  };

  #clearTransitionProperties = () => {
    this.style.removeProperty('--reveal-speed');
    this.style.removeProperty('--reveal-duration');
    this.style.removeProperty('--animation-duration');
  };

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #syncReevysHeaderState = () => {
    if (!mediaQueryLarge.matches) {
      delete this.dataset.reevysHeaderState;
      delete this.dataset.headerState;
      this.querySelector('.header-logo')?.removeAttribute('inert');
      return;
    }

    // The search drawer locks the page scroller; retain its underlying header state.
    if (this.querySelector('.reevys-header-search[data-drawer-open]')) return;

    const rect = this.getBoundingClientRect();
    const nextState = rect.top <= -59 ? 'compact' : 'expanded';
    const visibleHeight = Math.min(rect.height, Math.max(56, rect.bottom));
    document.body.style.setProperty('--header-height', `${visibleHeight}px`);

    if (this.dataset.headerState !== nextState) {
      this.dataset.headerState = nextState;
    }
    const stickyState = nextState === 'compact' ? 'active' : 'inactive';
    if (this.dataset.stickyState !== stickyState) this.dataset.stickyState = stickyState;
    this.querySelector('.header-logo')?.toggleAttribute('inert', nextState === 'compact');
  };

  #updateScrollState = () => {
    this.#syncReevysHeaderState();

    if (mediaQueryLarge.matches) return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    this.#setTransitionProperties(Math.abs(this.getBoundingClientRect().top));

    if (isScrollingUp) {
      if (this.getBoundingClientRect().top > SCROLL_UP_THRESHOLD) {
        this.dataset.stickyState = 'active';
        this.#clearTransitionProperties();
      } else {
        this.dataset.stickyState = 'idle';
      }
    } else {
      if (this.dataset.stickyState === 'active') {
        this.dataset.scrollDirection = 'none';
        this.dataset.stickyState = 'active';
        return;
      }

      this.dataset.stickyState = 'idle';

      if (scrollTop - this.#lastScrollTop > SCROLL_DOWN_THRESHOLD) {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'down';
        this.#lastScrollTop = scrollTop;
      }
    }
  };

  #handleVariantUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const newHtml = event.detail.data.html;
    const variantFeaturedMedia = newHtml.querySelector(`[data-product-id="${event.detail.productId}"]`)?.dataset
      .featuredMedia;

    if (!variantFeaturedMedia) return;

    this.querySelector(`a[href^="/products"]`)?.setAttribute(
      'href',
      `/products/${event.detail.productHandle}?variant=${event.detail.resource.id}`
    );
  };
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

const headerGroup = document.querySelector('#header-group');
const header = document.querySelector('#header-component');
const headerGroupSections = document.querySelectorAll('#header-group .shopify-section');

if (headerGroup && header) {
  const resizeObserver = new ResizeObserver(() => {
    const totalHeight = Array.from(headerGroupSections).reduce((sum, section) => sum + section.getBoundingClientRect().height, 0);
    document.body.style.setProperty('--header-group-height', `${totalHeight}px`);
  });

  resizeObserver.observe(headerGroup);
  headerGroupSections.forEach((section) => resizeObserver.observe(section));
}

function getIntersectionRoot() {
  const node = document.querySelector('.page-wrapper');

  if (node && node instanceof HTMLElement) {
    const style = getComputedStyle(node);

    if (style.overflowY === 'auto') {
      return node;
    }
  }

  return null;
}

function onAnimationFrame(callback) {
  return () => requestAnimationFrame(callback);
}

function getScrollTop() {
  const node = getIntersectionRoot();

  if (node instanceof HTMLElement) {
    return node.scrollTop;
  }

  return window.scrollY || document.documentElement.scrollTop || 0;
}

function changeMetaThemeColor(color) {
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) metaThemeColor.setAttribute('content', color);
}

function renderSavedItems(dialog) {
  let items = [];
  try {
    const stored = JSON.parse(localStorage.getItem('reevys:wishlist:v1') || '[]');
    if (Array.isArray(stored)) items = stored;
  } catch {}
  const list = dialog.querySelector('.reevys-bookmarks-list');
  list.replaceChildren();
  for (const item of items) {
    const url = new URL(item.url || `/products/${item.handle}`, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/products/')) continue;
    const link = document.createElement('a');
    link.href = url.href;
    if (item.image) {
      const imageUrl = new URL(item.image, location.origin);
      if (imageUrl.protocol === 'https:') {
        const image = document.createElement('img');
        image.src = imageUrl.href;
        image.alt = item.title || '';
        image.loading = 'lazy';
        link.append(image);
      }
    }
    const title = document.createElement('span');
    title.textContent = item.title;
    link.append(title);
    list.append(link);
  }
  dialog.querySelector('.reevys-bookmarks-empty').hidden = list.childElementCount > 0;
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('.reevys-bookmark');
  const dialog = document.getElementById('reevys-bookmarks-dialog');
  if (!dialog) return;
  if (trigger) {
    renderSavedItems(dialog);
    dialog.showModal();
  } else if (event.target === dialog) {
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  }
});

window.addEventListener('reevys:wishlist:change', () => {
  const dialog = document.getElementById('reevys-bookmarks-dialog');
  if (dialog?.open) renderSavedItems(dialog);
});
